import { useState, useEffect, useMemo } from 'react'

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Menu, Select, Text, Stack, ActionIcon, Box } from '@mantine/core'
import { ArrowUpDown, GripVertical, Trash2, Plus } from 'lucide-react'

import DNButton from 'src/components/Buttons/Button/DNButton'

export interface SortConfig {
  sortBy: string
  direction: 'ASC' | 'DESC'
}

export interface SortFieldOption {
  value: string
  label: string
}

interface SortByMenuProps {
  sortConfig: SortConfig[]
  onSortConfigChange: (newConfig: SortConfig[]) => void
  customFields?: Array<{
    customFieldId: string
    label: string
    isSearchable: boolean
    status: string
  }>
}

// Standard sort fields (alphabetically ordered by label)
const STANDARD_SORT_FIELDS: SortFieldOption[] = [
  { value: 'APPLICANT_NAME', label: 'Applicant Name' },
  { value: 'APPLICANT_STAGE', label: 'Applicant Stage' },
  { value: 'APPLICATION_DATE', label: 'Applied Date' },
  { value: 'AVAILABLE_FOR_WORK_DATE', label: 'Available for Work Date' },
  { value: 'CALL_COUNT', label: 'Call Count' },
  { value: 'DISQUALIFIED_DATE', label: 'Disqualified Date' },
  { value: 'EXPERIENCE', label: 'Experience' },
  { value: 'HIRED_DATE', label: 'Hired Date' },
  { value: 'LAST_CONTACT_DATE', label: 'Last Contact Date' },
  { value: 'APPLICANT_LAST_NAME', label: 'Last Name' },
  { value: 'LEAD_SOURCE', label: 'Lead Source' },
  { value: 'NOT_INTERESTED_DATE', label: 'Not Interested Date' },
  { value: 'POSITION', label: 'Position' },
  { value: 'RECRUITER', label: 'Recruiter' },
  { value: 'TERMINATED_DATE', label: 'Terminated Date' },
]

// Sortable sort item component
const SortableSortItem = ({
  sort,
  index,
  onDirectionChange,
  onRemove,
  sortFields,
}: {
  sort: SortConfig & { id: string }
  index: number
  onDirectionChange: (
    index: number,
    direction: 'ASC' | 'DESC',
    e?: React.MouseEvent
  ) => void
  onRemove: (index: number, e?: React.MouseEvent) => void
  sortFields: SortFieldOption[]
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sort.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const fieldLabel =
    sortFields.find((f) => f.value === sort.sortBy)?.label || sort.sortBy

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2"
      role="listitem"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="button"
        tabIndex={0}
        aria-label="Drag to reorder"
      >
        <GripVertical size={16} className="text-gray-400" />
      </div>

      <Text className="min-w-[150px] flex-1 truncate py-1 text-xs font-medium text-doubleNickel-gray-800">
        {fieldLabel}
      </Text>

      <Select
        size="xs"
        value={sort.direction}
        onChange={(value) => onDirectionChange(index, value as 'ASC' | 'DESC')}
        data={[
          { value: 'ASC', label: 'ASC ↑' },
          { value: 'DESC', label: 'DESC ↓' },
        ]}
        style={{ width: '50%' }}
        onClick={(e) => e.stopPropagation()}
      />

      <ActionIcon
        size="xs"
        variant="subtle"
        color="red"
        onClick={(e) => onRemove(index, e)}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Trash2 size={14} />
      </ActionIcon>
    </div>
  )
}

const SortByMenu = ({ sortConfig, onSortConfigChange, customFields = [] }: SortByMenuProps) => {
  const [showAddField, setShowAddField] = useState(false)
  const [newSortField, setNewSortField] = useState<string>('')
  const [newSortDirection, setNewSortDirection] = useState<'ASC' | 'DESC'>(
    'ASC'
  )
  const [selectKey, setSelectKey] = useState(0) // Key to force re-render

  // Generate custom field sort options
  const customFieldSortOptions = useMemo(() => {
    if (!customFields || customFields.length === 0) return []

    return customFields
      .filter((f) => f.isSearchable && f.status === 'ACTIVE')
      .map((field) => ({
        value: `CUSTOM_FIELD_${field.customFieldId}`,
        label: field.label,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [customFields])

  // Combine standard and custom field sort options
  const SORT_FIELDS = useMemo(() => {
    return [...STANDARD_SORT_FIELDS, ...customFieldSortOptions]
  }, [customFieldSortOptions])

  // Update when sortConfig changes from outside
  useEffect(() => {
    setNewSortField('')
    setNewSortDirection('ASC')
    setShowAddField(false)
    setSelectKey((prev) => prev + 1)
  }, [sortConfig])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = sortConfig.findIndex((s, i) => `sort-${i}` === active.id)
      const newIndex = sortConfig.findIndex((s, i) => `sort-${i}` === over.id)

      const newConfig = arrayMove(sortConfig, oldIndex, newIndex)
      onSortConfigChange(newConfig) // Apply immediately
    }
  }

  const handleDragStart = (event: DragStartEvent) => {
    // Prevent menu from closing during drag
    event.activatorEvent?.stopPropagation()
  }

  const handleDragOver = (_event: DragOverEvent) => {
    // Prevent menu from closing during drag over
    // Note: DragOverEvent doesn't have stopPropagation, but we can prevent it at the container level
  }

  const handleDirectionChange = (
    index: number,
    direction: 'ASC' | 'DESC',
    e?: React.MouseEvent
  ) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    const newConfig = sortConfig.map((sort, i) =>
      i === index ? { ...sort, direction } : sort
    )
    onSortConfigChange(newConfig) // Apply immediately
  }

  const handleRemoveSort = (index: number, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    const newConfig = sortConfig.filter((_, i) => i !== index)
    onSortConfigChange(newConfig) // Apply immediately

    // Force re-render to update available fields
    setNewSortField('')
    setShowAddField(false)
  }

  const handleAddField = (field: string) => {
    if (!field) return

    // Check if field already exists
    if (sortConfig.some((s) => s.sortBy === field)) {
      return
    }

    const newConfig = [
      ...sortConfig,
      { sortBy: field, direction: newSortDirection },
    ]
    onSortConfigChange(newConfig) // Apply immediately
  }

  const availableFields = useMemo(
    () =>
      SORT_FIELDS.filter((field) => {
        return !sortConfig.some((s) => {
          // For standard fields, check sortBy
          if (s.sortBy) {
            return s.sortBy === field.value
          }
          // For custom fields, check if field.value matches CUSTOM_FIELD_{customFieldId}
          if (s.customFieldId && field.value.startsWith('CUSTOM_FIELD_')) {
            return field.value === `CUSTOM_FIELD_${s.customFieldId}`
          }
          return false
        })
      }),
    [sortConfig, SORT_FIELDS]
  )

  // Add IDs for DnD
  const sortConfigWithIds = sortConfig.map((sort, index) => ({
    ...sort,
    id: `sort-${index}`,
  }))

  return (
    <Menu
      shadow="md"
      position="bottom-end"
      width={400}
      closeOnItemClick={false}
    >
      <Menu.Target>
        <DNButton variant="outline">
          <ArrowUpDown
            size={14}
            className="fill-none stroke-doubleNickel-gray-700"
          />
          Sort
          <div className="rounded-lg bg-doubleNickel-gray-100 px-1 py-0.5 text-[10px] text-doubleNickel-gray-500">
            {sortConfig.length}
          </div>
        </DNButton>
      </Menu.Target>

      <Menu.Dropdown className="rounded-lg">
        <div className="flex flex-col gap-2 py-2">
          <div className="flex flex-row items-center pr-4 text-sm">
            <Menu.Label>Sort</Menu.Label>
            <div className="ml-auto flex items-center gap-2">
              {!showAddField && availableFields.length > 0 && (
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  // color="gray"
                  onClick={() => setShowAddField(true)}
                >
                  <Plus size={18} />
                </ActionIcon>
              )}
            </div>
          </div>

          <div className="px-3">
            <Stack gap="xs" mb={showAddField ? 'xs' : 'md'}>
              {sortConfig.length > 0 && (
                <div
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  role="button"
                  tabIndex={0}
                  aria-label="Drag to reorder"
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                  >
                    <SortableContext
                      items={sortConfigWithIds.map((s) => s.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Stack gap="xs">
                        {sortConfigWithIds.map((sort, index) => (
                          <SortableSortItem
                            key={sort.id}
                            sort={sort}
                            index={index}
                            onDirectionChange={handleDirectionChange}
                            onRemove={handleRemoveSort}
                            sortFields={SORT_FIELDS}
                          />
                        ))}
                      </Stack>
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {showAddField && availableFields.length > 0 && (
                <div className="flex items-center gap-2">
                  <div
                    className="cursor-grab text-gray-400"
                    style={{ width: '16px' }}
                  >
                    <GripVertical size={16} className="invisible" />
                  </div>
                  <div className="min-w-[150px] flex-1">
                    <Select
                      key={`field-${selectKey}`}
                      size="xs"
                      placeholder="Select field"
                      data={availableFields}
                      value={newSortField}
                      onChange={(value) => {
                        if (value) {
                          handleAddField(value)
                        }
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      comboboxProps={{
                        withinPortal: false,
                        position: 'bottom-start',
                      }}
                      defaultDropdownOpened={true}
                    />
                  </div>
                  <Select
                    key={`direction-${selectKey}`}
                    size="xs"
                    placeholder="Direction"
                    data={[
                      { value: 'ASC', label: 'ASC ↑' },
                      { value: 'DESC', label: 'DESC ↓' },
                    ]}
                    value={newSortDirection}
                    onChange={(value) =>
                      setNewSortDirection(value as 'ASC' | 'DESC')
                    }
                    comboboxProps={{
                      withinPortal: false,
                    }}
                    style={{ width: '50%' }}
                  />
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    style={{ visibility: 'hidden' }}
                  >
                    <Trash2 size={14} />
                  </ActionIcon>
                </div>
              )}
            </Stack>
          </div>

          {sortConfig.length === 0 && availableFields.length === 0 && (
            <Text size="sm" c="dimmed" ta="center">
              All sort fields are in use
            </Text>
          )}
        </div>
      </Menu.Dropdown>
    </Menu>
  )
}

export default SortByMenu
